/**
 * Display a compound days + minutes value as a human-readable string.
 * Only shows parts that are non-zero, except always shows at least one part.
 * Examples: "16d 6h 24m", "20d", "6h 24m", "30m", "0m"
 */
export const displayDaysAndTime = (
  wholeDays,
  remainderMinutes,
  abbr = { d: 'd', h: 'h', m: 'm' },
) => {
  const absDays = Math.abs(wholeDays ?? 0);
  const absMins = Math.abs(remainderMinutes ?? 0);
  const hours = Math.floor(absMins / 60);
  const mins = absMins % 60;
  const sign = wholeDays < 0 ? '-' : '';

  const parts = [];
  if (absDays > 0) parts.push(`${absDays}${abbr.d}`);
  if (hours > 0) parts.push(`${hours}${abbr.h}`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}${abbr.m}`);

  return `${sign}${parts.join(' ')}`;
};

/**
 * Convert decimal hours to compound representation (whole days + remainder minutes).
 * Uses Math.round for minutes, so maximum error is 30 seconds.
 * Prefer server-provided compound fields when available.
 */
export const hoursToDaysAndTime = (decimalHours, hoursInDay) => {
  if (!hoursInDay || hoursInDay <= 0) {
    return { wholeDays: 0, remainderMinutes: Math.round(Math.abs(decimalHours ?? 0) * 60) };
  }
  const abs = Math.abs(decimalHours ?? 0);
  let halfDays = Math.floor((abs / hoursInDay) * 2) / 2;
  const remainderHours = abs - halfDays * hoursInDay;
  let remainderMinutes = Math.round(remainderHours * 60);

  const minutesInHalfDay = Math.round(hoursInDay * 30);
  if (remainderMinutes >= minutesInHalfDay) {
    halfDays += 0.5;
    remainderMinutes -= minutesInHalfDay;
  }

  let wholeDays = halfDays;
  if (decimalHours < 0) wholeDays = -wholeDays;
  return { wholeDays, remainderMinutes };
};

/**
 * Format decimal hours as compound display using hoursInDay.
 * Convenience wrapper combining hoursToDaysAndTime + displayDaysAndTime.
 */
export const displayHoursAsDaysAndTime = (
  decimalHours,
  hoursInDay,
  abbr = { d: 'd', h: 'h', m: 'm' },
) => {
  const { wholeDays, remainderMinutes } = hoursToDaysAndTime(decimalHours, hoursInDay);
  return displayDaysAndTime(wholeDays, remainderMinutes, abbr);
};

/**
 * Build the abbreviations object from intl + messages.
 * Messages must contain DaysAbbreviated, HoursAbbreviated, MinutesAbbreviated.
 */
export const getTimeAbbr = (intl, messages) => ({
  d: intl.formatMessage(messages.DaysAbbreviated),
  h: intl.formatMessage(messages.HoursAbbreviated),
  m: intl.formatMessage(messages.MinutesAbbreviated),
});
